import { LitElement, TemplateResult, html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { locationIcon, editIcon } from "@/assets/icons";
import {
  IProfileWidgetCustomization,
  USER_ACCOUNT_SETTINGS_WIDGETS_LAYOUT,
} from "@/types";

import {
  CARD_CONTAINER_CLASS,
  CARD_HEADER_CONTAINER_CLASS,
  CARD_HEADER_TITLE_CLASS,
  CARD_INPUT_LABEL_CLASS,
} from "../classes";

import { updateUser } from "@/service";
import { userContext, UserContextValue } from "@/context";
import { consume } from "@lit/context";
import { cn, showDestructiveToast } from "@/utils";

export interface LocationInfo {
  location: string;
  zoneInfo: string;
}

@customElement("sb-location-info")
export class SbLocationInfo extends LitElement {
  @property({ type: Object }) locationInfo!: LocationInfo;

  @property({ type: Boolean }) showIcons: boolean = true;

  @property({ type: String }) layout: USER_ACCOUNT_SETTINGS_WIDGETS_LAYOUT =
    USER_ACCOUNT_SETTINGS_WIDGETS_LAYOUT.ROW;

  @property({ type: Object })
  content: IProfileWidgetCustomization | null = null;

  @consume({ context: userContext, subscribe: true })
  private userContextValue!: UserContextValue;

  @state() private isEditing = false;
  @state() private isSaving = false;

  @state()
  private formData: LocationInfo = {
    location: "",
    zoneInfo: "",
  };

  @state()
  private errors: Partial<Record<keyof LocationInfo, string>> = {};

  createRenderRoot() {
    return this;
  }

  private handleEdit() {
    if (this.isSaving) return;

    this.formData = { ...this.locationInfo };
    this.errors = {};
    this.isEditing = true;
  }

  private handleCancel() {
    if (this.isSaving) return;

    this.isEditing = false;
    this.errors = {};
  }

  private updateField<K extends keyof LocationInfo>(key: K, value: string) {
    this.formData = {
      ...this.formData,
      [key]: value,
    };
  }

  private validate(): boolean {
    const errors: Partial<Record<keyof LocationInfo, string>> = {};
    this.errors = errors;
    return Object.keys(errors).length === 0;
  }

  private handleSave = async () => {
    if (this.isSaving) return;

    if (!this.validate()) return;

    this.isSaving = true;

    try {
      const updatedUser = await updateUser(this.userContextValue.identifier!, {
        address: {
          ...this.userContextValue.user?.address,
          locality: this.formData.location,
        },
        zoneinfo: this.formData.zoneInfo,
      });

      this.locationInfo = {
        location: updatedUser.address?.locality ?? "",
        zoneInfo: updatedUser.zoneinfo ?? "",
      };

      this.isEditing = false;
    } catch (error) {
      console.error("Failed to update location info", error);

      let errorMessage =
        "Failed to update location information. Please try again.";

      if (error instanceof Error && "status" in error) {
        const apiError = error as any;
        errorMessage =
          apiError.data?.error?.message ?? apiError.message ?? errorMessage;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      showDestructiveToast(errorMessage);
    } finally {
      this.isSaving = false;
    }
  };

  private renderField(label: string, key: keyof LocationInfo) {
    if (this.layout === USER_ACCOUNT_SETTINGS_WIDGETS_LAYOUT.ROW) {
      return html`
        <div class="sb-flex sb-w-full sb-account-settings-info-card-rows">
          <rtg-typography-p
            ><div
              class="${CARD_INPUT_LABEL_CLASS} sb-account-settings-field-label"
            >
              ${label}
            </div></rtg-typography-p
          >
          <div class="sb-account-settings-field-input-wrapper">
            <rtg-field ?invalid=${!!this.errors[key]}>
              <rtg-input
                id=${key}
                .value=${this.formData[key]}
                ?disabled=${this.isSaving}
                @input=${(e: Event) =>
                  this.updateField(key, (e.target as HTMLInputElement).value)}
              ></rtg-input>

              ${this.errors[key]
                ? html`
                    <rtg-field-error> ${this.errors[key]} </rtg-field-error>
                  `
                : null}
            </rtg-field>
          </div>
        </div>
      `;
    }

    return html`
      <div
        class="sb-account-settings-field-column sb-account-settings-info-card-rows"
      >
        <div class="sb-account-settings-field-label">
          <rtg-typography-p
            ><div class=${CARD_INPUT_LABEL_CLASS}>
              ${label}
            </div></rtg-typography-p
          >
        </div>

        <div class="sb-account-settings-field-input-wrapper">
          <rtg-field ?invalid=${!!this.errors[key]}>
            <rtg-input
              id=${key}
              .value=${this.formData[key]}
              ?disabled=${this.isSaving}
              @input=${(e: Event) =>
                this.updateField(key, (e.target as HTMLInputElement).value)}
            ></rtg-input>

            ${this.errors[key]
              ? html` <rtg-field-error> ${this.errors[key]} </rtg-field-error> `
              : null}
          </rtg-field>
        </div>
      </div>
    `;
  }

  private renderFooter(): TemplateResult {
    return html`
      <rtg-card-footer>
        <div class="sb-account-settings-card-actions">
          <rtg-button
            variant="default"
            ?disabled=${this.isSaving}
            @click=${this.handleSave}
            size="lg"
          >
            ${this.isSaving
              ? html`
                  <rtg-spinner class="rtg-icon-inline-start"></rtg-spinner>
                  Saving
                `
              : "Save"}
          </rtg-button>

          <rtg-button
            variant="ghost"
            ?disabled=${this.isSaving}
            @click=${this.handleCancel}
            size="lg"
          >
            Cancel
          </rtg-button>
        </div>
      </rtg-card-footer>
    `;
  }

  render() {
    const fields = [
      { label: "Location", key: "location" as const },
      { label: "Zone Info", key: "zoneInfo" as const },
    ];

    return html`
      <rtg-card editing=${this.isEditing} part="location-info-card">
        <div class=${CARD_CONTAINER_CLASS}>
          <rtg-card-header>
            <div class=${CARD_HEADER_CONTAINER_CLASS}>
              <rtg-card-title>
                <div class=${CARD_HEADER_TITLE_CLASS}>
                  ${this.content?.icon}
                  <span>${this.content?.title ?? "Location"}</span>
                </div>
              </rtg-card-title>

              ${!this.isEditing
                ? html`
                    <rtg-button
                      variant="link"
                      size="default"
                      aria-label="Edit"
                      @click=${this.handleEdit}
                    >
                      <div class="sb-flex sb-gap-1 sb-items-center">
                        ${editIcon()} Edit
                      </div>
                    </rtg-button>
                  `
                : null}
            </div>
          </rtg-card-header>

          <rtg-card-content>
            ${this.isEditing
              ? html`
                  <div
                    class=${this.layout ===
                    USER_ACCOUNT_SETTINGS_WIDGETS_LAYOUT.ROW
                      ? "sb-grid sb-account-settings-row-layout sb-account-settings-form-body"
                      : "sb-account-settings-column-layout sb-account-settings-form-body"}
                  >
                    ${fields.map((f) => this.renderField(f.label, f.key))}
                  </div>
                `
              : html`
                  <div
                    class=${this.layout ===
                    USER_ACCOUNT_SETTINGS_WIDGETS_LAYOUT.ROW
                      ? "sb-grid sb-account-settings-row-layout sb-account-settings-form-body"
                      : "sb-account-settings-column-layout sb-account-settings-form-body"}
                  >
                    <div
                      class=${this.layout ===
                      USER_ACCOUNT_SETTINGS_WIDGETS_LAYOUT.ROW
                        ? "sb-flex sb-w-full sb-account-settings-info-card-rows"
                        : "sb-account-settings-info-card-rows"}
                    >
                      <rtg-typography-p
                        ><div
                          class=${cn(
                            CARD_INPUT_LABEL_CLASS,
                            "sb-account-settings-field-label",
                          )}
                        >
                          Location
                        </div></rtg-typography-p
                      >
                      <div>
                        <rtg-typography-p>
                          ${this.locationInfo.location
                            ? this.locationInfo.location
                            : html`<p class=${CARD_INPUT_LABEL_CLASS}>
                                Not Added
                              </p>`}
                        </rtg-typography-p>
                      </div>
                    </div>

                    <div
                      class=${this.layout ===
                      USER_ACCOUNT_SETTINGS_WIDGETS_LAYOUT.ROW
                        ? "sb-flex sb-w-full sb-account-settings-info-card-rows"
                        : "sb-account-settings-info-card-rows"}
                    >
                      <rtg-typography-p
                        ><div
                          class=${cn(
                            CARD_INPUT_LABEL_CLASS,
                            "sb-account-settings-field-label",
                          )}
                        >
                          Zone Info
                        </div></rtg-typography-p
                      >
                      <div>
                        <rtg-typography-p>
                          ${this.locationInfo.zoneInfo
                            ? this.locationInfo.zoneInfo
                            : html`<p class=${CARD_INPUT_LABEL_CLASS}>
                                Not Added
                              </p>`}
                        </rtg-typography-p>
                      </div>
                    </div>
                  </div>
                `}
          </rtg-card-content>

          ${this.isEditing ? html`<rtg-separator></rtg-separator>` : null}
          ${this.isEditing ? this.renderFooter() : null}
        </div>
      </rtg-card>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "sb-location-info": SbLocationInfo;
  }
}
